/**
 * The 40-card baraja and the 8 reyes / 4 reyes mapping.
 *
 * Cards keep their physical identity forever — `3o` is the 3 of oros in the
 * stock, in the discard pile, in the replay log and on the sprite sheet. The
 * variant is applied at comparison time by `effectiveRank`, which drives both
 * ordering and point value, so 8R/4R is one table with no second code path
 * (backend.md §1.2).
 */
import { nextInt } from './rng.js';
import { at } from './util.js';
/** No 8s and no 9s in a baraja española. */
export const RANKS = [1, 2, 3, 4, 5, 6, 7, 10, 11, 12];
export const SUIT_CODES = ['o', 'c', 'e', 'b'];
export const SUIT_BY_CODE = {
    o: 'oros',
    c: 'copas',
    e: 'espadas',
    b: 'bastos',
};
/** Deterministic construction order: suit-major, then rank ascending. */
export const DECK = SUIT_CODES.flatMap((suit) => RANKS.map((rank) => `${rank}${suit}`));
const CARD_BY_ID = new Map(DECK.map((id) => [
    id,
    {
        id,
        rank: Number(id.slice(0, -1)),
        suit: SUIT_BY_CODE[id.slice(-1)],
    },
]));
export function parseCard(id) {
    const card = CARD_BY_ID.get(id);
    if (card === undefined) {
        throw new RangeError(`unknown card id: ${String(id)}`);
    }
    return card;
}
/**
 * Ordering key. 8R: the 2 plays as an As, the 3 plays as a Rey. 4R: identity.
 * Used for grande, chica and pares alike.
 */
export function effectiveRank(rank, variant) {
    if (variant === '8r') {
        if (rank === 2)
            return 1;
        if (rank === 3)
            return 12;
    }
    return rank;
}
/** Juego/punto value: every figure counts 10, everything else its effective rank. */
export function cardValue(rank, variant) {
    const effective = effectiveRank(rank, variant);
    return effective >= 10 ? 10 : effective;
}
/** Pure Fisher-Yates. Returns the shuffled copy and the advanced PRNG state. */
export function shuffle(cards, rng) {
    const out = cards.slice();
    let seed = rng;
    for (let i = out.length - 1; i > 0; i -= 1) {
        const [j, next] = nextInt(seed, i + 1);
        seed = next;
        const held = at(out, i);
        out[i] = at(out, j);
        out[j] = held;
    }
    return [out, seed];
}
//# sourceMappingURL=cards.js.map